import React, { ReactNode, useState } from "react";
import Table from "@mui/material/Table";
import Paper from "@mui/material/Paper";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Checkbox from "@mui/material/Checkbox";
import { DeviceDetailDataTypes, DeviceValueTypes } from "pages/Devices/devices.config";
import { PoolDeviceTypes } from "pages/Pools/pools.config";
import { SORT_DIRECTION } from "utils/constants";
import LoadingSpinner from "components/LoadingSpinner/loading-spinner.component";
import { UserListTypes } from "pages/Users/users.config";
import { TableColumnTypes } from "pages/configs/configs";
import { TableWrapper, NoDataWrapper, TableHeaderCell } from "./table.style";
import { DeviceDataTypes } from "pages/device-lookup/device-lookup.config";
import { SoftwareVersionTypes } from "pages/PowerUser/software/version/versions.api";
import { InventoryValueTypes } from "pages/PowerUser/inventory/inventory.api";

interface CustomTableProps {
  children: ReactNode;
  rowsPerPageOptions?: number[];
  totalCount?: number;
  onPageChange?: (arg: number) => void;
  page?: number;
  onRowsPerPageChange?: (arg: number) => void;
  rowsPerPage?: number;
  boxShadow?: boolean;
}

const CustomTable = ({
  children,
  rowsPerPageOptions,
  totalCount,
  onPageChange,
  boxShadow,
  page,
  onRowsPerPageChange,
  rowsPerPage,
  ...rest
}: CustomTableProps): JSX.Element => {
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    onPageChange?.(newPage);
  };

  const handleChangeRowsPerPage = (event: { target: { value: string | number } }) => {
    onRowsPerPageChange?.(+event.target.value);
    onPageChange?.(0);
  };

  const getTablePagination = (): JSX.Element => {
    if (!totalCount || !rowsPerPage || page === undefined || !rowsPerPageOptions) {
      return <></>;
    }

    return (
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    );
  };

  return (
    <Paper
      style={boxShadow ? { boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px" } : {}}
      sx={{ width: "100%", overflow: "hidden", position: "relative", boxShadow: 0 }}
    >
      <TableWrapper>
        <Table sx={{ boxShadow: 0 }} stickyHeader aria-label="sticky table" {...rest}>
          {children}
        </Table>
      </TableWrapper>

      {getTablePagination()}
    </Paper>
  );
};

interface CustomTableHeaderProps {
  columns: TableColumnTypes[];
  onRequestSort?: (order: string, orderBy: string) => void;
  onSelectAll?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isAllSelected?: boolean;
  hasCheckbox?: boolean;
  isIndeterminate?: boolean;
  defaultOrder?: "asc" | "desc";
  defaultOrderBy?: string;
}

const CustomTableHeader = ({
  columns,
  onRequestSort,
  onSelectAll,
  isAllSelected,
  hasCheckbox,
  isIndeterminate,
  defaultOrder,
  defaultOrderBy,
  ...rest
}: CustomTableHeaderProps): JSX.Element => {
  const [order, setOrder] = useState<"desc" | "asc" | undefined>(defaultOrder || SORT_DIRECTION.ASC);
  const [orderBy, setOrderBy] = useState<string | undefined>(defaultOrderBy);

  const createSortHandler = (property: string) => () => {
    // update internal state
    const isAsc = orderBy === property && order === SORT_DIRECTION.ASC;
    setOrder(isAsc ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC);
    setOrderBy(property);

    // sent event to outside
    onRequestSort?.(isAsc ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC, property);
  };

  const getTableHeaders = (label: any): any => {
    if (!label) {
      return;
    }

    return label;
  };

  return (
    <TableHead {...rest}>
      <TableRow>
        {columns.map(({ id, className, align, width, label, isSort }: any) => {
          return (
            <React.Fragment key={id || `header-${label || "empty"}`}>
              {!label && id === "" && hasCheckbox ? (
                <TableHeaderCell padding="checkbox" sx={{ width: width }}>
                  <Checkbox
                    color="primary"
                    checked={isAllSelected || false}
                    indeterminate={isIndeterminate || false}
                    onChange={onSelectAll}
                  />
                </TableHeaderCell>
              ) : !label ? (
                <TableHeaderCell sx={{ width: width }} />
              ) : (
                <TableHeaderCell className={className} align={align}>
                  {id && label && isSort ? (
                    <TableSortLabel
                      sx={{ width: width, justifyContent: align }}
                      active={orderBy === id}
                      direction={orderBy === id ? order : SORT_DIRECTION.ASC}
                      onClick={createSortHandler(id)}
                    >
                      {getTableHeaders(label)}
                    </TableSortLabel>
                  ) : (
                    <TableSortLabel sx={{ width: width }}>{getTableHeaders(label)}</TableSortLabel>
                  )}
                </TableHeaderCell>
              )}
            </React.Fragment>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

interface CustomTableBodyProps {
  children: ReactNode;
  isLoading: boolean;
  deviceData?:
    | DeviceValueTypes[]
    | DeviceDetailDataTypes[]
    | UserListTypes[]
    | PoolDeviceTypes[]
    | DeviceDataTypes[]
    | SoftwareVersionTypes[]
    | InventoryValueTypes[];
}

const CustomTableBody = ({ children, isLoading, deviceData, ...rest }: CustomTableBodyProps): JSX.Element => (
  <TableBody sx={{ boxShadow: 0 }} {...rest}>
    {!isLoading && !deviceData?.length && (
      <TableRow>
        <NoDataWrapper colSpan={12} style={{ height: 150 }}>
          No records found!
        </NoDataWrapper>
      </TableRow>
    )}
    {isLoading && (
      <TableRow>
        <NoDataWrapper colSpan={12} style={{ height: 150, position: "relative" }}>
          <LoadingSpinner />
        </NoDataWrapper>
      </TableRow>
    )}
    {children}
  </TableBody>
);

CustomTable.Header = CustomTableHeader;

CustomTable.Body = CustomTableBody;

export default CustomTable;
